<template>
  <section class="view-archived-snapshot" aria-label="Archived component snapshot">
    <div ref="snapshot" v-show="!interactiveRestored" class="snapshot-static" v-html="html"></div>
    <div ref="live" :class="{ 'snapshot-live-probe': !interactiveRestored }" :aria-hidden="!interactiveRestored || null" :inert="!interactiveRestored">
      <ViewRichBlock :block-id="blockId" :source="currentSource || snapshotSource" />
    </div>
    <p v-if="changed" class="snapshot-state"><span aria-hidden="true">◷</span> Shown as it appeared when this View was created</p>
  </section>
</template>

<script>
import { restoreSnapshotStyles } from '../../utils/viewSnapshotStyles';
import ViewRichBlock from './ViewRichBlock.vue';
import { semanticDomSignature, sourceFingerprint } from '../../utils/viewSnapshotCompare';

export default {
  name: 'ViewArchivedSnapshot',
  components: { ViewRichBlock },
  props: {
    html: { type: String, required: true },
    blockId: { type: String, required: true },
    snapshotSource: { type: Object, required: true },
    currentSource: { type: Object, default: null },
  },
  data() {
    return { interactiveRestored: false, changed: false, lastLiveSignature: '', stableChecks: 0 };
  },
  watch: { html() { this.checkSnapshot(); }, currentSource() { this.checkSnapshot(); } },
  mounted() {
    this._observer = new MutationObserver(() => {
      if (this.interactiveRestored) return;
      this.refreshSnapshot(); if (this._canRestore) this.scheduleComparison();
    });
    this.checkSnapshot();
  },
  beforeUnmount() {
    this._comparisonVersion = (this._comparisonVersion || 0) + 1;
    this._observer?.disconnect();
    window.clearTimeout(this._comparisonTimer);
  },
  methods: {
    async checkSnapshot() {
      const version = this._comparisonVersion = (this._comparisonVersion || 0) + 1;
      this._canRestore = false; this._observer?.disconnect(); window.clearTimeout(this._comparisonTimer);
      this.interactiveRestored = false; this.changed = false; this.lastLiveSignature = ''; this.stableChecks = 0;
      await this.$nextTick();
      if (version !== this._comparisonVersion) return;
      const live = this.$refs.live;
      if (!live?.isConnected) return;
      this.refreshSnapshot();
      this._observer?.observe(live, { childList: true, subtree: true, characterData: true });
      if (!this.currentSource) { this.changed = true; return; }
      if (this.snapshotSource.source_fingerprint) {
        const fingerprint = await sourceFingerprint(this.currentSource);
        if (version !== this._comparisonVersion) return;
        if (fingerprint !== this.snapshotSource.source_fingerprint) { this.changed = true; return; }
        // A saved playing video has different controls from its initial cover.
        // Matching source fingerprints preserve the same video and description.
        if (this.currentSource.media?.some(media => media.kind === 'video')) {
          this.interactiveRestored = true;
          this._observer?.disconnect();
          return;
        }
      }
      this._canRestore = true; this.scheduleComparison();
    },
    refreshSnapshot() { restoreSnapshotStyles(this.$refs.snapshot, this.$refs.live); },
    scheduleComparison() {
      window.clearTimeout(this._comparisonTimer);
      this._comparisonTimer = window.setTimeout(() => this.compareStableContent(), 700);
    },
    compareStableContent() {
      if (!this._canRestore || this.changed || this.interactiveRestored) return;
      const liveSignature = semanticDomSignature(this.$refs.live);
      if (!liveSignature) return;
      if (liveSignature !== this.lastLiveSignature) {
        this.lastLiveSignature = liveSignature;
        this.stableChecks = 0;
        this.scheduleComparison();
        return;
      }
      this.stableChecks += 1;
      if (this.stableChecks < 1) {
        this.scheduleComparison();
        return;
      }
      const snapshotSignature = semanticDomSignature(this.$refs.snapshot);
      this.changed = Boolean(snapshotSignature && snapshotSignature !== liveSignature);
      // Restore native controls only when the mounted component contains the
      // same preserved content. A changed source keeps its historical snapshot.
      this.interactiveRestored = Boolean(snapshotSignature && !this.changed);
      if (this.interactiveRestored) this._observer?.disconnect();
    },
  },
};
</script>

<style scoped>
.view-archived-snapshot { width: 100%; overflow: hidden; }
.snapshot-static :deep(button),
.snapshot-static :deep(input),
.snapshot-static :deep(select),
.snapshot-static :deep(textarea) { pointer-events: none; }
.snapshot-live-probe {
  position: fixed;
  left: -100000px;
  top: 0;
  width: min(38rem, 80vw);
  visibility: hidden;
  pointer-events: none;
}
.snapshot-state {
  margin: 0.85rem 0 0;
  color: #858585;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.64rem;
  line-height: 1.4;
}
.snapshot-state span { color: var(--site-accent); }
:global(body.dark-mode .snapshot-state) { color: #999; }
</style>
